import { Formik, Form } from 'formik';
import { FormikInput, FormikTextarea, Toggle, PrimaryButton, SecondaryButton } from "../UI";
import PrimaryModal from '../UI/Modal/PrimaryModal';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useUserContext } from '../../contexts/UserContext';
import { notificationSchema } from '../../schemas';
import { createNotification, updateNotification } from '../../utils/APIs';
import { useContentfulContext } from '../../contexts/ContentfulContext';

const NotificationModal = ({ setSelectedAction, selectedNotification, getNotifications }) => {
    const [submitError, setSubmitError] = useState('');
    const { userData } = useUserContext();
    const { content } = useContentfulContext();

    const isEdit = Boolean(selectedNotification);

    const initialValues = {
        type: 'global', // Fixed to 'global' for both create and edit
        title: selectedNotification?.title || '',
        description: selectedNotification?.description || '',
        deep_link: selectedNotification?.deep_link || '',
        category: selectedNotification?.category || '',
        is_email_required: false,
        email_recipients: selectedNotification?.email_recipients || [],
    };

    const addNotification = async (payload, actions) => {
        try {
            const result = await createNotification(userData.token, userData.selectedOrganization.org_token, payload);
            if (result.status) {
                toast.success(result.message);
                getNotifications();
                actions.resetForm();
                setSubmitError('');
                setSelectedAction('');
            }
        } catch (error) {
            setSubmitError(content.ADMIN_NOTIFICATIONS.create_error);
            toast.error(submitError);
            console.error('Create notification error:', error);
            actions.setSubmitting(false);
        }
    };

    const editNotification = async (payload, actions) => {
        try {
            const result = await updateNotification(userData.token, userData.selectedOrganization.org_token, selectedNotification._id, payload);
            if (result.status) {
                toast.success(result.message);
                getNotifications();
                actions.resetForm();
                setSubmitError('');
                setSelectedAction('');
            }
        } catch (error) {
            setSubmitError(content.ADMIN_NOTIFICATIONS.edit_error);
            toast.error(submitError);
            console.error('Edit notification error:', error);
            actions.setSubmitting(false);
        }
    };

    const handleSubmit = async (values, actions) => {
        const payload = { ...values };

        // If editing, remove 'is_email_required' and 'email_recipients'
        if (isEdit) {
            delete payload.is_email_required;
            delete payload.email_recipients;
            delete payload.category;
            editNotification(payload, actions)
        } else {
            // Convert email_recipients from string to array
            if (payload.email_recipients && payload.email_recipients.length > 0) {
                payload.email_recipients = payload.email_recipients.split(',').map(email => email.trim()) || [];
            }
            addNotification(payload, actions)
        }
    };

    return (
        <PrimaryModal
            title={isEdit ? content.ADMIN_NOTIFICATIONS.edit_notification : content.ADMIN_NOTIFICATIONS.create_notification}
            hideModal={() => setSelectedAction('')}
        >
            <Formik
                initialValues={initialValues}
                validationSchema={notificationSchema}
                onSubmit={handleSubmit}
                validateOnBlur
                validateOnChange
            >
                {({ errors, touched, isSubmitting, values, setFieldValue }) => (
                    <Form className="space-y-4 p-6">
                        {/* Type Field */}
                        <FormikInput
                            id="type"
                            name="type"
                            label={content.ADMIN_NOTIFICATIONS.form.type}
                            disabled
                        />

                        {/* Title Field */}
                        <FormikInput
                            id="title"
                            name="title"
                            label={content.ADMIN_NOTIFICATIONS.form.title}
                            mandatory
                        />

                        {/* Description Field */}
                        <FormikTextarea
                            id="description"
                            name="description"
                            label={content.ADMIN_NOTIFICATIONS.form.description}
                            rows={4}
                            mandatory
                        />

                        {/* Deep Link Field */}
                        <FormikInput
                            type="url"
                            id="deep_link"
                            name="deep_link"
                            label={content.ADMIN_NOTIFICATIONS.form.deep_link}
                        />

                        {/* Category Field */}
                        {!isEdit && (
                            <FormikInput
                                id="category"
                                name="category"
                                label={content.ADMIN_NOTIFICATIONS.form.category}
                            />
                        )}

                        {/* Additional Fields for Create */}
                        {!isEdit && (
                            <>
                                {/* Is Email Required Field */}
                                <div className="flex items-center">
                                    <Toggle
                                        id="is_email_required"
                                        name="is_email_required"
                                        checked={Boolean(values.is_email_required)}
                                        onChange={(e) => {
                                            setFieldValue('is_email_required', e.target.checked);
                                            if (!e.target.checked) {
                                                setFieldValue('email_recipients', '');
                                            }
                                        }}
                                        label={content.ADMIN_NOTIFICATIONS.form.is_email_reqired}
                                        size="sm"
                                    />
                                </div>

                                {/* Email Recipients Field */}
                                <FormikInput
                                    id="email_recipients"
                                    name="email_recipients"
                                    label={content.ADMIN_NOTIFICATIONS.form.email_recipients}
                                    disabled={!values.is_email_required}
                                    placeholder="example1@domain.com, example2@domain.com"
                                    value={Array.isArray(values.email_recipients) ? values.email_recipients.join(', ') : (values.email_recipients || '')}
                                    onChange={(e) => {
                                        const emails = e.target.value.split(',').map((email) => email.trim());
                                        setFieldValue('email_recipients', emails);
                                    }}
                                />
                            </>
                        )}

                        {/* Error Message */}
                        {submitError && (
                            <div className="mt-2 text-sm text-red-500">{submitError}</div>
                        )}

                        {/* Buttons */}
                        <div className="flex justify-end space-x-4">
                            <SecondaryButton handleClick={() => setSelectedAction('')}>
                                {content.ADMIN_NOTIFICATIONS.cancel}
                            </SecondaryButton>
                            <PrimaryButton type="submit" disabled={isSubmitting}>
                                {isSubmitting ? 'Submitting...' : isEdit ? content.ADMIN_NOTIFICATIONS.edit_notification : content.ADMIN_NOTIFICATIONS.create_notification}
                            </PrimaryButton>
                        </div>
                    </Form>
                )
                }
            </Formik>
        </PrimaryModal>
    );
};

export default NotificationModal;
